// dependencies
import axios from "axios";
import { Map, List } from "immutable";
import { GenerateProcess } from "redux-process";

// config
import config from "Config";

// ui
import ui from "./ui";

// actions
import { updateUI } from "Actions/ui";
import { createDesktopNotification } from "Actions/notifications";

// selectors
import { getUI, getPageName } from "Selectors/ui";
import {
	getEnvironmentBranch,
	getPagesize,
	graphSelectionRange,
	getGraphSelectionCurrentPage,
	getMeanResponseTime,
	getGraphSelectionRangeIndexes,
} from "./selectors";

// resync pointer
let resyncTimer = null;

export const changeUI = (keyPath, value) => (dispatch, getState) => {
	const state = getState();
	const pageName = getPageName(state);
	const newKeyPath = [pageName].concat(keyPath);

	dispatch(updateUI(newKeyPath, value));
};

/**
 * Ping each environment and figure out what branch they are on.
 */
export const checkEnvironmentBranches = () => async (dispatch, getState) => {
	const loganEnv = {
		name: "logan",
		fullName: "logan",
		label: "Logan",
		cdn: null,
		host: "https://wolverine.talent.performyard.com",
	};

	[...config.environments, loganEnv].forEach(async (env) => {
		const url = `${env.host}/health`;

		try {
			const resp = await axios.get(url);
			const nextHash = resp.headers["x-git-hash"];
			const currentHash = getEnvironmentBranch(getState(), env.name).get("hash", "");

			if (currentHash && currentHash !== "" && currentHash !== nextHash) {
				dispatch(createDesktopNotification(`A change was detected on ${env.fullName}.`));
			}

			const hash = resp.headers["x-git-hash"] || "";
			const branch = resp.headers["x-git-branch"] || "";

			dispatch(changeUI(["environmentBranches", env.name], Map({ hash, branch })));
		} catch (ermahgerd) {
			dispatch(changeUI(["environmentBranches", env.name], Map({ hash: "", branch: "" })));
		}
	});
};

/**
 * Populates the mean response time and request count values based on the
 * selection. If the selection range index is empty, then it does the full
 * range.
 */
export const populateRequestStatistics = () => async (dispatch, getState) => {
	let state = getState();
	const requests = getMeanResponseTime(state);
	const selectionIndexes = getGraphSelectionRangeIndexes(state);

	// the not set values handles the case where no selection is made
	const slicedRequests = requests.slice(selectionIndexes.get(0, 0), selectionIndexes.get(1, requests.size));
	const totalRequestCount = slicedRequests.reduce(
		(accumulator, currentValue) => accumulator + currentValue.get("value", 0),
		0
	);

	const totalRequestMean =
		slicedRequests
			.map((val) => val.get("mean", 0) * val.get("value", 1))
			.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / totalRequestCount;

	state = getState();
	const pageUI = getUI(state);

	const nextUI = pageUI.withMutations((mutable) => {
		mutable.setIn(["environmentRequestCount"], totalRequestCount);
		mutable.setIn(["environmentMeanResponseTime"], totalRequestMean);
	});

	dispatch(changeUI([], nextUI));
};

/**
 * Get the data for the request time bucket graph
 */
export const fetchRequestBuckets = () => async (dispatch, getState) => {
	const requestsPerMinute = await dispatch(GenerateProcess("GetRequestsPerMinute"));

	const state = getState();
	const pageUI = getUI(state);

	const nextUI = pageUI.withMutations((mutable) => {
		mutable.setIn(["requestsPerMinute"], requestsPerMinute.response);
	});

	dispatch(changeUI([], nextUI));
	dispatch(populateRequestStatistics());
};

/**
 * Fetches the request within the graph range. the graph range is stored inside
 * the page state.
 */
export const getRequestsInRange = () => async (dispatch, getState) => {
	const state = getState();

	const selectionRange = graphSelectionRange(state);
	const lowerBoundary = selectionRange.get(0);
	const upperBoundary = selectionRange.get(1);

	const pageSize = getPagesize(state);
	const currentPage = getGraphSelectionCurrentPage(state);
	const request = await dispatch(
		GenerateProcess("GetRequests", {
			limit: pageSize,
			skip: (currentPage - 1) * pageSize,
			startTime: lowerBoundary,
			endTime: upperBoundary,
			sort: "-delta_time",
		})
	);

	const nextState = Map({
		currentPage,
		requests: request.response.get("requests"),
		count: request.response.get("requestsCount"),
	});

	dispatch(changeUI(["graphSelection"], nextState));
};

/**
 * Updates the range in the state then dispatches a fetch request action. If
 * the `lowerBoundary` is null, that means we are resetting the results. This
 * will also set the indexes for the lower and upper boundary so we can update
 * the mean response times and request counts.
 *
 * @param {number} lowerBoundary - time in seconds for lower boundary
 * @param {number} upperBoundary - time in seconds for upper boundary
 */
export const updateRequestsRange = (lowerBoundary, upperBoundary) => async (dispatch, getState) => {
	const state = getState();
	const pageUI = getUI(state);
	const initialUI = ui;

	if (lowerBoundary === null) {
		const resetState = pageUI.withMutations((mutable) => {
			mutable.setIn(["graphSelection"], initialUI.get("graphSelection"));
			mutable.setIn(["graphSelectionRange"], initialUI.get("graphSelectionRange"));
			mutable.setIn(["graphSelectionRangeIndexes"], initialUI.get("graphSelectionRangeIndexes"));
		});

		dispatch(changeUI([], resetState));
		dispatch(populateRequestStatistics());
		return;
	}

	// find the index of the upper and lower boundary
	let lowerBoundaryIndex = null;
	let upperBoundaryIndex = null;
	const requests = getMeanResponseTime(state);
	const requestsCount = requests.size;

	for (let ii = 0; ii < requestsCount; ii += 1) {
		const date = requests.getIn([ii, "date"], null);
		const time = date ? date.getTime() / 1000 : Infinity;

		if (!lowerBoundaryIndex && lowerBoundary <= time) {
			lowerBoundaryIndex = Math.max(0, ii - 1);
		}

		if (!upperBoundaryIndex && lowerBoundaryIndex && upperBoundary <= time) {
			upperBoundaryIndex = Math.max(0, ii - 1);
			break;
		}
	}

	const nextState = pageUI.withMutations((mutable) => {
		mutable.setIn(["graphSelection"], initialUI.get("graphSelection"));
		mutable.setIn(["graphSelectionRange"], List([lowerBoundary, upperBoundary]));
		mutable.setIn(["graphSelectionRangeIndexes"], List([lowerBoundaryIndex, upperBoundaryIndex]));
	});

	dispatch(changeUI([], nextState));
	dispatch(getRequestsInRange());
	dispatch(populateRequestStatistics());
};

/**
 * Updates the page number of the requests then dispatches a fetch request
 * action.
 *
 * @param {number} page - page number to change to
 */
export const updateRequestsPage = (page) => async (dispatch) => {
	dispatch(changeUI(["graphSelection", "currentPage"], page));
	dispatch(getRequestsInRange());
};

/**
 * Get the total company count
 */
export const getTotalCompanyCount = () => async (dispatch) => {
	const request = await dispatch(GenerateProcess("TotalCompanyCount"));
	const { companyCount, userCount, maxUserCount } = request.response;
	dispatch(changeUI(["environmentCompanyCount"], companyCount));
	dispatch(changeUI(["environmentActiveUserCount"], userCount));
	dispatch(changeUI(["environmentTotalUserCount"], maxUserCount));
};

/**
 * Fetch (or refetch) graph data and redraw.
 */
export const initializeGraphs = () => (dispatch) => {
	dispatch(getTotalCompanyCount());
	dispatch(fetchRequestBuckets());
	dispatch(updateRequestsRange(null));
};

/**
 * Resync data on the dashboard
 */
export const resyncData = () => (dispatch, getState) => {
	dispatch(checkEnvironmentBranches());
	dispatch(GenerateProcess("getDeployments"));

	if (getGraphSelectionRangeIndexes(getState()).size < 2) {
		dispatch(fetchRequestBuckets());
	}
};

/**
 * Schedule the data resync
 */
export const resyncDataScheduler = () => (dispatch) => {
	const resyncRate = 10; // seconds

	resyncTimer = setInterval(() => {
		dispatch(resyncData());
	}, resyncRate * 1000);
};

/**
 * Runs on unmount. Cancels timers.
 */
export const cleanup = () => () => {
	clearInterval(resyncTimer);
};

/**
 * Initialize page.
 */
export const init = (graphWidth) => (dispatch) => {
	// set the initial UI
	let initialUI = ui;
	initialUI = initialUI.set("graphWidth", graphWidth);
	dispatch(changeUI([], initialUI));

	dispatch(initializeGraphs());
	dispatch(resyncDataScheduler());
	dispatch(GenerateProcess("getDeployments"));

	// const resyncRate = 30; // seconds

	// const resyncInterval = setInterval(() => {
	//     dispatch(initializeGraphs());
	// }, resyncRate * 1000);
};
